import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, ROLES, papelDeAcesso } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiPost, apiPut, apiDelete, fetchAllPaginated, fetchSchoolsSimple } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import RefreshButton from '../components/RefreshButton';
import SearchableSelect from '../components/SearchableSelect';
import { useDeleteModal } from '../hooks/useDeleteModal';
import { useEffectiveCity } from '../hooks/useEffectiveCity';
import { includesNormalized } from '../utils/normalizeText';

function Professores() {
  const { user } = useAuth();
  // Gestor e Secretário só visualizam, e o Diretor(a) só altera a situação
  // dos alunos: a API recusa a escrita dos três aqui.
  const podeEscrever = ![ROLES.GESTOR, ROLES.SECRETARIO, ROLES.DIRETOR].includes(user?.role);
  const { darkMode } = useTheme();
  const userCity = useEffectiveCity();
  const cityId = userCity?.id || null;

  // Estados
  const [professores, setProfessores] = useState([]);
  const [escolas, setEscolas] = useState([]);
  const [disciplinas, setDisciplinas] = useState([]);
  const [teacherSchools, setTeacherSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [filtroStatus, setFiltroStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal de criação/edição
  const [showModal, setShowModal] = useState(false);
  const [editingProfessor, setEditingProfessor] = useState(null);
  const [formData, setFormData] = useState({
    teacher_name: '',
    teacher_serial: '',
    status: 'active',
    subjects: [],
    schools: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Modal de exclusão
  const {
    isDeleteModalOpen,
    itemToDelete,
    openDeleteModal,
    closeDeleteModal
  } = useDeleteModal();

  // Modal de detalhes
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProfessor, setSelectedProfessor] = useState(null);

  // Mesclagem de cadastro duplicado. É o caminho certo para a mesma pessoa
  // cadastrada duas vezes: excluir uma delas levaria junto o diário, as notas
  // e a frequência lançados naquele cadastro.
  const [professorParaMesclar, setProfessorParaMesclar] = useState(null);
  const [duplicadoEscolhido, setDuplicadoEscolhido] = useState('');
  const [mesclando, setMesclando] = useState(false);
  const [erroMesclagem, setErroMesclagem] = useState('');

  // Buscar dados
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const cityParams = cityId ? { city: cityId } : {};
      const [professoresData, escolasData, disciplinasData, teacherSchoolsData] = await Promise.all([
        fetchAllPaginated('/teachers/', cityParams),
        fetchSchoolsSimple(cityId),
        fetchAllPaginated('/subjects/'),
        fetchAllPaginated('/teacher-schools/'),
      ]);

      setProfessores(professoresData);
      setEscolas(escolasData);
      setDisciplinas(disciplinasData);
      setTeacherSchools(teacherSchoolsData);
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [cityId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Obter escolas disponíveis para o usuário (filtradas por cidade/escolas atribuídas)
  const getAvailableSchools = () => {
    if (user?.role === ROLES.ADMIN) {
      return escolas;
    }
    if (papelDeAcesso(user?.role) === ROLES.SUPERVISOR) {
      // Supervisor: apenas escolas atribuídas a ele
      const assignedSchoolIds = user.assigned_schools || [];
      if (assignedSchoolIds.length > 0) {
        return escolas.filter(e => assignedSchoolIds.includes(e.id));
      }
      return escolas;
    }
    // Coordenador/Gestor: escolas da sua cidade
    if (userCity?.id) {
      return escolas.filter(e => e.id_city === userCity.id);
    }
    return escolas;
  };

  // Obter escolas de um professor
  const getProfessorEscolas = (professorId) => {
    return teacherSchools
      .filter(ts => ts.id_teacher === professorId && ts.status === 'active')
      .map(ts => {
        const escola = escolas.find(e => e.id === ts.id_school);
        return escola ? escola.school : ts.school_name || 'Escola não encontrada';
      });
  };

  // Obter IDs das escolas de um professor
  const getProfessorSchoolIds = (professorId) => {
    return teacherSchools
      .filter(ts => ts.id_teacher === professorId && ts.status === 'active')
      .map(ts => ts.id_school);
  };

  // Filtrar professores
  const filteredProfessores = professores.filter(prof => {
    const matchSearch = !searchTerm ||
      includesNormalized(prof.teacher_name, searchTerm) ||
      String(prof.teacher_serial).includes(searchTerm);

    const matchStatus = !filtroStatus || prof.status === filtroStatus;

    return matchSearch && matchStatus;
  });

  // Abrir modal para criar
  const handleCreate = () => {
    setEditingProfessor(null);
    const availableSchools = getAvailableSchools();
    setFormData({
      teacher_name: '',
      teacher_serial: '',
      status: 'active',
      subjects: [],
      // Auto-selecionar todas as escolas disponíveis para supervisores
      schools: papelDeAcesso(user?.role) === ROLES.SUPERVISOR ? availableSchools.map(e => e.id) : [],
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Abrir modal para editar
  const handleEdit = (professor) => {
    setEditingProfessor(professor);
    // Usa subject_ids (IDs estáveis) quando disponível; cai para o
    // matching por nome apenas como compatibilidade com respostas antigas
    // que ainda não trouxerem o campo.
    const subjectIds = Array.isArray(professor.subject_ids)
      ? professor.subject_ids
      : disciplinas
          .filter(d => professor.subject_details?.includes(d.subject_name))
          .map(d => d.id);
    const schoolIds = getProfessorSchoolIds(professor.id);
    setFormData({
      teacher_name: professor.teacher_name || '',
      teacher_serial: professor.teacher_serial || '',
      status: professor.status || 'active',
      subjects: subjectIds,
      schools: schoolIds,
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Validar formulário
  const validateForm = () => {
    const errors = {};
    if (!formData.teacher_name.trim()) errors.teacher_name = 'Nome é obrigatório';
    if (!formData.teacher_serial) errors.teacher_serial = 'Matrícula é obrigatória';
    if (formData.schools.length === 0) errors.schools = 'Selecione pelo menos uma escola';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Salvar professor
  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        teacher_name: formData.teacher_name.trim(),
        teacher_serial: parseInt(formData.teacher_serial),
        status: formData.status,
        subjects: formData.subjects,
        schools: formData.schools,
      };

      if (editingProfessor) {
        await apiPut(`/teachers/${editingProfessor.id}/`, payload);
      } else {
        await apiPost('/teachers/', payload);
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Erro ao salvar professor:', err);
      setFormErrors({
        submit: err.response?.data?.detail || err.response?.data?.teacher_serial?.[0] || 'Erro ao salvar professor. Tente novamente.',
      });
    } finally {
      setSaving(false);
    }
  };

  // Deletar professor
  const handleDelete = async (item) => {
    if (!item) return;

    try {
      await apiDelete(`/teachers/${item.id}/`);
      await fetchData();
    } catch (err) {
      console.error('Erro ao excluir professor:', err);
      throw err;
    }
  };

  // Funde o duplicado neste cadastro. O endpoint é o do RH — é a mesma
  // tabela (TbEmployee = TbTeacher), e a regra de fusão já vive lá.
  const handleMesclar = async () => {
    if (!professorParaMesclar || !duplicadoEscolhido) return;
    setMesclando(true);
    setErroMesclagem('');
    try {
      await apiPost(`/employees/${professorParaMesclar.id}/merge/`, {
        duplicate_id: Number(duplicadoEscolhido),
      });
      setProfessorParaMesclar(null);
      setDuplicadoEscolhido('');
      await fetchData();
    } catch (err) {
      console.error('Erro ao mesclar cadastros:', err);
      setErroMesclagem(
        err.response?.data?.error || err.message || 'Não foi possível mesclar.'
      );
    } finally {
      setMesclando(false);
    }
  };

  // Ver detalhes
  const handleViewDetails = (professor) => {
    setSelectedProfessor(professor);
    setShowDetailsModal(true);
  };

  // Toggle disciplina no formulário
  const toggleSubject = (subjectId) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subjectId)
        ? prev.subjects.filter(id => id !== subjectId)
        : [...prev.subjects, subjectId]
    }));
  };

  // Toggle escola no formulário
  const toggleSchool = (schoolId) => {
    setFormData(prev => ({
      ...prev,
      schools: prev.schools.includes(schoolId)
        ? prev.schools.filter(id => id !== schoolId)
        : [...prev.schools, schoolId]
    }));
  };

  // Estatísticas
  const totalProfessores = professores.length;
  const professoresAtivos = professores.filter(p => p.status === 'active').length;
  const professoresInativos = professores.filter(p => p.status !== 'active').length;

  if (loading) {
    return <LoadingSpinnerAdvanced message="Carregando professores..." darkMode={darkMode} />;
  }

  const availableSchools = getAvailableSchools();

  return (
    <div className={`min-h-screen p-4 sm:p-6 ${darkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
            Professores
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Gerencie os professores do sistema
            {userCity?.name && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]'}`}>
                {userCity.name}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <RefreshButton onClick={fetchData} />
          {podeEscrever && (
            <button
              onClick={handleCreate}
              className="px-4 py-2.5 bg-[#0974BA] text-white rounded-lg text-sm font-medium hover:bg-[#0974BA]/90 transition-colors flex items-center gap-2"
            >
              <span>+</span>
              Novo Professor
            </button>
          )}
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className={`p-4 rounded-xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
          <p className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total</p>
          <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-slate-800'}`}>{totalProfessores}</p>
        </div>
        <div className={`p-4 rounded-xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
          <p className={`text-xs font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Ativos</p>
          <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{professoresAtivos}</p>
        </div>
        <div className={`p-4 rounded-xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
          <p className={`text-xs font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>Inativos</p>
          <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{professoresInativos}</p>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <div className={`mb-6 p-4 rounded-xl border ${darkMode ? 'bg-[#E83A3C]/10 border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border-[#E83A3C]/20'}`}>
          <p className="text-[#E83A3C] text-sm">{error}</p>
          <button onClick={fetchData} className="text-[#0974BA] text-sm mt-2 hover:underline">Tentar novamente</button>
        </div>
      )}

      {/* Filtros */}
      <div className={`p-4 rounded-xl border mb-6 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
                : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
              placeholder="Buscar por nome ou matrícula..."
            />
          </div>
          <SearchableSelect
            value={filtroStatus}
            onChange={setFiltroStatus}
            options={[
              { value: 'active', label: 'Ativo' },
              { value: 'inactive', label: 'Inativo' },
            ]}
            emptyOptionLabel="Todos os status"
            darkMode={darkMode}
            className="min-w-[150px]"
          />
        </div>
      </div>

      {/* Tabela de Professores */}
      <div className={`rounded-xl border overflow-hidden ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}>
                <th className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Professor
                </th>
                <th className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Matrícula
                </th>
                <th className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Disciplinas
                </th>
                <th className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Escolas
                </th>
                <th className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Status
                </th>
                <th className={`text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
              {filteredProfessores.length === 0 ? (
                <tr>
                  <td colSpan="6" className={`text-center py-12 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {searchTerm || filtroStatus ? 'Nenhum professor encontrado com os filtros aplicados.' : 'Nenhum professor cadastrado.'}
                  </td>
                </tr>
              ) : (
                filteredProfessores.map((prof) => {
                  const escolasProf = getProfessorEscolas(prof.id);
                  return (
                    <tr
                      key={prof.id}
                      className={`transition-colors ${darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}
                    >
                      <td className={`py-3 px-4 ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]'}`}>
                            {prof.teacher_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <Link to={`/professores/${prof.id}`} className="font-medium text-sm hover:underline hover:text-[#0974BA]"
                            title="Painel: aulas lançadas, pendências, notas">
                            {prof.teacher_name}
                          </Link>
                        </div>
                      </td>
                      <td className={`py-3 px-4 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {prof.teacher_serial || '-'}
                      </td>
                      <td className={`py-3 px-4 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        <div className="flex flex-wrap gap-1">
                          {prof.subject_details && prof.subject_details.length > 0 ? (
                            prof.subject_details.map((sub, idx) => (
                              <span
                                key={idx}
                                className={`inline-block px-2 py-0.5 rounded-full text-xs ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]'}`}
                              >
                                {sub}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs opacity-50">-</span>
                          )}
                        </div>
                      </td>
                      <td className={`py-3 px-4 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        <div className="flex flex-wrap gap-1">
                          {escolasProf.length > 0 ? (
                            escolasProf.slice(0, 2).map((escola, idx) => (
                              <span key={idx} className="text-xs">{escola}{idx < Math.min(escolasProf.length, 2) - 1 ? ', ' : ''}</span>
                            ))
                          ) : (
                            <span className="text-xs opacity-50">-</span>
                          )}
                          {escolasProf.length > 2 && (
                            <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>+{escolasProf.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                          prof.status === 'active'
                            ? darkMode ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
                            : darkMode ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'
                        }`}>
                          {prof.status === 'active' ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/professores/${prof.id}`}
                            className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                            title="Painel: aulas lançadas, pendências, notas"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </Link>
                          <button
                            onClick={() => handleViewDetails(prof)}
                            className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                            title="Ver detalhes"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          {podeEscrever && (
                            <>
                              <button
                                onClick={() => handleEdit(prof)}
                                className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                                title="Editar"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setProfessorParaMesclar(prof);
                                  setDuplicadoEscolhido('');
                                  setErroMesclagem('');
                                }}
                                className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                                title="Mesclar cadastro duplicado neste"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                              </button>
                              <button
                                onClick={() => openDeleteModal(prof)}
                                className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-[#E83A3C]/10 text-slate-400 hover:text-[#E83A3C]' : 'hover:bg-[#E83A3C]/5 text-slate-400 hover:text-[#E83A3C]'}`}
                                title="Excluir"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Rodapé da tabela */}
        <div className={`px-4 py-3 border-t ${darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'}`}>
          <p className="text-sm">
            Exibindo {filteredProfessores.length} de {totalProfessores} professores
          </p>
        </div>
      </div>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-lg rounded-2xl ${darkMode ? 'bg-slate-800' : 'bg-white'} shadow-2xl max-h-[90vh] overflow-y-auto`}>
            {/* Header do Modal */}
            <div className={`p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                  {editingProfessor ? 'Editar Professor' : 'Novo Professor'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {userCity?.name && (
                <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Cidade: <span className="font-medium">{userCity.name}</span>
                </p>
              )}
            </div>

            {/* Formulário */}
            <div className="p-6 space-y-4">
              {/* Nome */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Nome Completo *
                </label>
                <input
                  type="text"
                  value={formData.teacher_name}
                  onChange={(e) => setFormData({ ...formData, teacher_name: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.teacher_name ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Nome completo do professor"
                />
                {formErrors.teacher_name && (
                  <p className="text-[#E83A3C] text-xs mt-1">{formErrors.teacher_name}</p>
                )}
              </div>

              {/* Matrícula */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Matrícula *
                </label>
                <input
                  type="number"
                  value={formData.teacher_serial}
                  onChange={(e) => setFormData({ ...formData, teacher_serial: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.teacher_serial ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Número de matrícula"
                />
                {formErrors.teacher_serial && (
                  <p className="text-[#E83A3C] text-xs mt-1">{formErrors.teacher_serial}</p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Status
                </label>
                <SearchableSelect
                  value={formData.status}
                  onChange={(v) => setFormData({ ...formData, status: v })}
                  options={[
                    { value: 'active', label: 'Ativo' },
                    { value: 'inactive', label: 'Inativo' },
                  ]}
                  placeholder="Selecione"
                  darkMode={darkMode}
                  className="w-full"
                />
              </div>

              {/* Escolas */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Escolas *
                </label>
                <div className={`p-3 rounded-lg border max-h-40 overflow-y-auto ${darkMode ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'} ${formErrors.schools ? 'border-[#E83A3C]' : ''}`}>
                  {availableSchools.length === 0 ? (
                    <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma escola disponível</p>
                  ) : (
                    <div className="space-y-2">
                      {availableSchools.map(escola => (
                        <label key={escola.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.schools.includes(escola.id)}
                            onChange={() => toggleSchool(escola.id)}
                            className="rounded border-slate-300 text-[#0974BA] focus:ring-[#0974BA]"
                          />
                          <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {escola.school}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {formErrors.schools && (
                  <p className="text-[#E83A3C] text-xs mt-1">{formErrors.schools}</p>
                )}
              </div>

              {/* Disciplinas */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Disciplinas
                </label>
                <div className={`p-3 rounded-lg border max-h-40 overflow-y-auto ${darkMode ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  {disciplinas.length === 0 ? (
                    <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma disciplina disponível</p>
                  ) : (
                    <div className="space-y-2">
                      {disciplinas.map(disc => (
                        <label key={disc.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.subjects.includes(disc.id)}
                            onChange={() => toggleSubject(disc.id)}
                            className="rounded border-slate-300 text-[#0974BA] focus:ring-[#0974BA]"
                          />
                          <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {disc.subject_name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {formErrors.submit && (
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
                  <p className="text-[#E83A3C] text-sm">{formErrors.submit}</p>
                </div>
              )}
            </div>

            {/* Footer do Modal */}
            <div className={`p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'} flex justify-end gap-3`}>
              <button
                onClick={() => setShowModal(false)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${darkMode
                  ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2.5 bg-[#0974BA] text-white rounded-lg text-sm font-medium hover:bg-[#0974BA]/90 transition-colors disabled:opacity-50"
              >
                {saving ? 'Salvando...' : editingProfessor ? 'Atualizar' : 'Cadastrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      {showDetailsModal && selectedProfessor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-lg rounded-2xl ${darkMode ? 'bg-slate-800' : 'bg-white'} shadow-2xl max-h-[90vh] overflow-y-auto`}>
            <div className={`p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                  Detalhes do Professor
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Avatar e Nome */}
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]'}`}>
                  {selectedProfessor.teacher_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                    {selectedProfessor.teacher_name}
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Matrícula: {selectedProfessor.teacher_serial}
                  </p>
                </div>
              </div>

              {/* Info grid */}
              <div className={`grid grid-cols-2 gap-4 p-4 rounded-lg ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                <div>
                  <p className={`text-xs font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Status</p>
                  <span className={`inline-block mt-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                    selectedProfessor.status === 'active'
                      ? darkMode ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
                      : darkMode ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'
                  }`}>
                    {selectedProfessor.status === 'active' ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <div>
                  <p className={`text-xs font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Cadastrado em</p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {selectedProfessor.created_at ? new Date(selectedProfessor.created_at).toLocaleDateString('pt-BR') : '-'}
                  </p>
                </div>
              </div>

              {/* Disciplinas */}
              <div>
                <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Disciplinas</p>
                <div className="flex flex-wrap gap-2">
                  {selectedProfessor.subject_details && selectedProfessor.subject_details.length > 0 ? (
                    selectedProfessor.subject_details.map((sub, idx) => (
                      <span
                        key={idx}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]'}`}
                      >
                        {sub}
                      </span>
                    ))
                  ) : (
                    <span className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma disciplina vinculada</span>
                  )}
                </div>
              </div>

              {/* Escolas */}
              <div>
                <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Escolas</p>
                <div className="space-y-1">
                  {getProfessorEscolas(selectedProfessor.id).length > 0 ? (
                    getProfessorEscolas(selectedProfessor.id).map((escola, idx) => (
                      <p key={idx} className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {escola}
                      </p>
                    ))
                  ) : (
                    <span className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma escola vinculada</span>
                  )}
                </div>
              </div>
            </div>

            <div className={`p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'} flex justify-end`}>
              <button
                onClick={() => setShowDetailsModal(false)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${darkMode
                  ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão com Senha */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        item={itemToDelete}
        itemName={itemToDelete?.teacher_name}
        itemType="Professor"
        warningMessage="Só é possível excluir um cadastro sem nenhum vínculo. Se a pessoa está cadastrada duas vezes, use 'Mesclar cadastro duplicado'. Digite sua senha para confirmar."
      />

      {/* Mesclar duplicado */}
      {professorParaMesclar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-lg rounded-2xl p-6 ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'}`}>
            <h2 className={`text-xl font-semibold mb-1 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Mesclar cadastro duplicado
            </h2>
            <p className={`text-sm mb-5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Tudo do cadastro duplicado — turmas, disciplinas, lotações, grade
              horária e conta de acesso — passa para{' '}
              <strong className={darkMode ? 'text-slate-200' : 'text-slate-700'}>
                {professorParaMesclar.teacher_name}
              </strong>
              , e o duplicado é excluído. O diário, as notas e a frequência
              continuam onde estão.
            </p>

            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Cadastro duplicado (o que vai desaparecer)
            </label>
            <SearchableSelect
              value={duplicadoEscolhido}
              onChange={setDuplicadoEscolhido}
              options={professores
                .filter(p => p.id !== professorParaMesclar.id)
                .map(p => ({
                  value: p.id,
                  label: `${p.teacher_name}${p.teacher_serial ? ` • ${p.teacher_serial}` : ''}`,
                }))}
              emptyOptionLabel="Escolha o cadastro..."
              searchPlaceholder="Buscar por nome ou matrícula..."
              darkMode={darkMode}
            />

            <p className={`text-xs mt-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Confira os dois antes: a fusão não tem desfazer.
            </p>

            {erroMesclagem && (
              <div className={`mt-4 p-3 rounded-lg text-sm ${darkMode
                ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20 text-[#E83A3C]'
                : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20 text-[#E83A3C]'}`}>
                {erroMesclagem}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700/10">
              <button
                onClick={() => { setProfessorParaMesclar(null); setErroMesclagem(''); }}
                disabled={mesclando}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
              >
                Cancelar
              </button>
              <button
                onClick={handleMesclar}
                disabled={mesclando || !duplicadoEscolhido}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white ${
                  mesclando || !duplicadoEscolhido
                    ? 'bg-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg'
                }`}
              >
                {mesclando ? 'Mesclando...' : 'Mesclar cadastros'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Professores;
